import useOutsideClick from "hooks/click-outside";
import React from "react";
import { AiOutlineClose } from "react-icons/ai";
import { FaCheck, FaChevronRight } from "react-icons/fa";

interface CoreConcept {
  id: number;
  name: string;
  description: string;
  type: string;
  category: {
    id: number;
    name: string;
    color: string;
    imageId: any;
  };
  averagePoints: number;
  initialAverage: number;
  totalAnswers: number;
  totalQuestions: number;
  awardedBadge: any;
}

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  coreConcepts?: CoreConcept[];
};

const CoreConceptModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  coreConcepts = [],
}) => {
  const modalRef = useOutsideClick(onClose);

  if (!isOpen) return null;

  // Apply conditions and transform the data - filter out concepts with averagePoints = 3 (100% completion)
  const transformedConcepts = coreConcepts
    .filter((concept) => concept.averagePoints < 3) // Hide concepts with 100% completion
    .map((concept) => ({
      title: concept.name,
      description: concept.description,
      value: Math.round((concept.averagePoints / 3) * 100),
      completed: concept.awardedBadge !== null,
      category: concept.category.name,
      categoryColor: concept.category.color,
      initialAverage: concept.initialAverage,
      currentAverage: concept.averagePoints,
      progress:
        concept.averagePoints > concept.initialAverage
          ? "improved"
          : concept.averagePoints === concept.initialAverage
          ? "same"
          : "declined",
    }));

  return (
    <div
    className={`fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300 ${
      isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
    }`}
    style={{ zIndex: 'calc(var(--z-modal) + 1)' }}
    >
      <div
        ref={modalRef}
        className={`relative bg-white px-6 py-8 rounded-lg shadow-lg w-[90%] sm:w-[375px] h-[80vh] transform transition-transform duration-300 ${
          isOpen ? "scale-100" : "scale-95"
        }`}
      > 
        <div className="flex justify-between items-center mb-4">
          {/* Title */}
          <h2 className="text-lg font-semibold text-primaryBlueText">Builder</h2>

          <button
            onClick={onClose}
            className="text-primaryBlueText"
          >
            <AiOutlineClose size={20}     className="  transition text-primaryBlueText" />
          </button>
        </div>
        {/* Close Button */}

        {/* Progress List */}
        <div className="space-y-4 max-h-[68vh] overflow-y-auto scrollbar-hide custom-scrollbar ">
          {transformedConcepts.map((item, index) => (
            <div key={index} className="mr-1">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-primaryBlueText">
                  {item.title}
                </span>
                <span className="text-xs text-primaryBlueText">
                  {item.value}%
                </span>
              </div>

              {/* Progress Bar */}
              <div className="flex items-center gap-2">
                <div className="w-full h-2 rounded-full bg-[#E5E7EB] overflow-hidden">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      item.completed ? "bg-primaryBlueBg" : "bg-primaryBlueBg"
                    }`}
                    style={{ width: `${item.value}%` }}
                  ></div>
                </div>
                {/* <button className="min-w-[28px] min-h-[28px] flex items-center justify-center rounded-md bg-[#F2A64F] text-white">
                  {item.completed ? (
                    <FaCheck size={14} />
                  ) : (
                    <FaChevronRight size={14} />
                  )}
                </button> */}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CoreConceptModal;
